import type { OntologyEntry } from '../types.js';

/**
 * Story confidence ladder — absorbed from IntelHub's `confidence_label` pg enum
 * (Phase 1 Semantic Dedup, 2026-04-18). Computed deterministically by TierResolver
 * from the source-tier mix across articles in a story cluster (dedup spec §5.8).
 *
 * This is the STRUCTURAL / CORROBORATION axis. The ARTICLE-level epistemic axis
 * is a separate dimension: `article-confidence` (renamed from the original
 * `confidence` dimension in Phase 5). See spec §4.
 *
 * DB-bridge convention (spec §3.2 Decision 2):
 *   Ontology slugs are all kebab-case; IntelHub's pg enum `confidence_label`
 *   still uses `single_source` (underscore) for one value. The `single-source`
 *   entry below carries `metadata.dbEnumSlug = 'single_source'` as the explicit
 *   bridge. Consumers should prefer `entry.metadata?.dbEnumSlug ?? entry.slug.replace(/-/g, '_')`
 *   when converting to the DB-compatible value.
 */
export const storyConfidence: OntologyEntry[] = [
  {
    slug: 'established',
    name: 'Established',
    description: 'Confirmed by at least one authoritative source (T1), or 3+ independent research-tier (T2) orgs. Highest-trust story-level signal.',
    dimension: 'story-confidence',
    sortOrder: 0,
    metadata: { numericValue: 4 },
  },
  {
    slug: 'corroborated',
    name: 'Corroborated',
    description: 'Supported by 2+ research-tier orgs, or 1 research + 2+ specialist orgs. Multi-org independent confirmation.',
    dimension: 'story-confidence',
    sortOrder: 1,
    metadata: { numericValue: 3 },
  },
  {
    slug: 'reported',
    name: 'Reported',
    description: 'Single research-tier org, or 2+ specialist-tier orgs. One credible source without independent corroboration.',
    dimension: 'story-confidence',
    sortOrder: 2,
    metadata: { numericValue: 2 },
  },
  {
    slug: 'single-source',
    name: 'Single Source',
    description: 'One specialist source, or 2+ general sources. Claim exists but is not yet corroborated by credible outlets.',
    dimension: 'story-confidence',
    sortOrder: 3,
    metadata: { numericValue: 1, dbEnumSlug: 'single_source' },
  },
  {
    slug: 'unverified',
    name: 'Unverified',
    description: 'Single general-tier source only. Weakest-trust signal — treat as provisional pending corroboration.',
    dimension: 'story-confidence',
    sortOrder: 4,
    metadata: { numericValue: 0 },
  },
];
